/** @jsxImportSource @emotion/react */
import React from "react";
import ReactDOM from "react-dom";
import { useHistory, useLocation } from "react-router-dom";

// Hooks
import useAppRoutes from "../common/hooks/use-app-routes";
import { useAuth } from "../auth/auth";
import useOverflowMenu from "../common/hooks/useOverflowMenu";

// Components
import NavItem from "./nav-item";
import HamburgerMenu from "../common/components/hamburger-menu";

const HAMBURGER_WIDTH = 44;

const Navigation = (props) => {
    const routesMetaData = useAppRoutes();
    const { pathname } = useLocation();
    const history = useHistory();
    const { userType, features } = useAuth();

    // Props
    const { isAdminHeader } = props;

    // Items del menú superior filtrados por permisos y por cabecera (admin / cliente)
    const navItems = routesMetaData
        .filter(route => route.isAdminRoute === isAdminHeader)
        .map(({ text, to }) => {
            const feature = to.substring(1);
            const hasAccess = userType === "admin" || (userType === "client" && (features.indexOf(feature) >= 0)) || feature === "home";
            return hasAccess
                ? {
                    text,
                    to,
                    isActive: pathname === to,
                    onClick: () => history.push(to)
                }
                : null;
        })
        .filter(Boolean);

    const {
        containerRef,
        hamburgerRef,
        setMeasureRef,
        visibleItems,
        overflowItems,
        hasOverflow
    } = useOverflowMenu(navItems, {
        safetyMargin: 12,
        gap: 8,
        hamburgerFallbackWidth: HAMBURGER_WIDTH,
        debounceMs: 120
    });

    const handleOverflowItemClick = (item) => {
        if (item && item.onClick) {
            item.onClick();
        }
    };

    // Medición de anchos reales: se renderiza en un portal para que no afecte al layout de la cabecera
    const measurement = (
        <div
            aria-hidden="true"
            css={{
                position: "fixed",
                top: 0,
                left: 0,
                visibility: "hidden",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center"
            }}
        >
            {navItems.map((item, index) => (
                <div key={`measure-${index}`} ref={(el) => setMeasureRef(index, el)} css={{ flexShrink: 0 }}>
                    <NavItem text={item.text} isActive={false} to={item.to} />
                </div>
            ))}
        </div>
    );

    return (
        <>
            {ReactDOM.createPortal(measurement, document.body)}

            {/* Menú superior visible: los items que caben + hamburguesa con el resto */}
            <nav
                ref={containerRef}
                css={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    overflow: "hidden"
                }}
            >
                {visibleItems.map((item, index) => (
                    <div key={`item-${index}`} css={{ flexShrink: 0 }}>
                        <NavItem text={item.text} isActive={item.isActive} to={item.to} />
                    </div>
                ))}
                {hasOverflow && (
                    <div ref={hamburgerRef} css={{ marginLeft: "auto", flexShrink: 0 }}>
                        <HamburgerMenu items={overflowItems} onItemClick={handleOverflowItemClick} />
                    </div>
                )}
            </nav>
        </>
    );
};

export default Navigation;
